import { useCallback, useEffect, useRef, useState } from 'react'

export interface CarouselImageData {
  url: string
  originalIndex: number
  isCurrentImage: boolean
}

export interface UseCarouselResult {
  currentIndex: number
  displayIndex: number
  isTransitioning: boolean
  imageData: CarouselImageData[]
  slideWidth: number
  totalSlides: number
  showNav: boolean
  goNext(): void
  goPrev(): void
  setIndex(index: number): void
  swipeHandlers: {
    onTouchStart: (e: React.TouchEvent) => void
    onTouchMove: (e: React.TouchEvent) => void
    onTouchEnd: () => void
  }
}

const MIN_SWIPE_DISTANCE = 50
const TRANSITION_DURATION = 300

const useSwipeGesture = (onSwipeLeft: () => void, onSwipeRight: () => void) => {
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE

    if (isLeftSwipe) onSwipeLeft()
    if (isRightSwipe) onSwipeRight()
  }

  return { onTouchStart, onTouchMove, onTouchEnd }
}

export function useCarousel(imageUrls: string[]): UseCarouselResult {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [transitionDisplayIndex, setTransitionDisplayIndex] = useState<
    number | null
  >(null)
  const timeoutRef = useRef<NodeJS.Timeout>()

  const count = imageUrls.length
  const hasMultipleImages = count > 1

  const infiniteImages = hasMultipleImages
    ? [imageUrls[count - 1], ...imageUrls, imageUrls[0]]
    : imageUrls

  const totalSlides = infiniteImages.length
  const slideWidth = totalSlides > 0 ? 100 / totalSlides : 100
  const showNav = hasMultipleImages

  const displayIndex =
    transitionDisplayIndex ?? (hasMultipleImages ? currentIndex + 1 : 0)

  const getOriginalIndex = (index: number): number => {
    if (!hasMultipleImages) return index
    if (index === 0) return count - 1
    if (index === totalSlides - 1) return 0
    return index - 1
  }

  const imageData: CarouselImageData[] = infiniteImages.map((url, index) => {
    const originalIndex = getOriginalIndex(index)
    return {
      url,
      originalIndex,
      isCurrentImage: originalIndex === currentIndex
    }
  })

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const completeTransition = useCallback(
    (newIndex: number, newDisplayIndex: number | null) => {
      timeoutRef.current = setTimeout(() => {
        setCurrentIndex(newIndex)
        setTransitionDisplayIndex(newDisplayIndex)
        setIsTransitioning(false)
      }, TRANSITION_DURATION)
    },
    []
  )

  const slide = useCallback(
    (direction: 'next' | 'prev') => {
      if (isTransitioning || !hasMultipleImages) return

      setIsTransitioning(true)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      if (direction === 'next') {
        const nextIndex = (currentIndex + 1) % count

        if (nextIndex === 0) {
          setTransitionDisplayIndex(totalSlides - 1)
          completeTransition(0, null)
        } else {
          setCurrentIndex(nextIndex)
          setTransitionDisplayIndex(null)
          completeTransition(nextIndex, null)
        }
      } else {
        const prevIndex = (currentIndex - 1 + count) % count

        if (prevIndex === count - 1) {
          setTransitionDisplayIndex(0)
          completeTransition(count - 1, null)
        } else {
          setCurrentIndex(prevIndex)
          setTransitionDisplayIndex(null)
          completeTransition(prevIndex, null)
        }
      }
    },
    [
      isTransitioning,
      hasMultipleImages,
      currentIndex,
      count,
      totalSlides,
      completeTransition
    ]
  )

  const goNext = useCallback(() => slide('next'), [slide])
  const goPrev = useCallback(() => slide('prev'), [slide])

  const setIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= count) return
      setCurrentIndex(index)
      setTransitionDisplayIndex(null)
    },
    [count]
  )

  const swipeHandlers = useSwipeGesture(goNext, goPrev)

  return {
    currentIndex,
    displayIndex,
    isTransitioning,
    imageData,
    slideWidth,
    totalSlides,
    showNav,
    goNext,
    goPrev,
    setIndex,
    swipeHandlers
  }
}
